/**
 * Messaging session utilities — fetch chat URLs and parse MDI messaging responses.
 */

import { getMessagingAppUrl } from "@repo/mdi";
import type { MessagingSessionResult } from "..";

/**
 * Fetches a messaging session URL for a patient, optionally scoped to a specific case.
 * Handles the MDI getMessagingAppUrl call, parses the case_id from the auth_link,
 * and extracts the verification code.
 *
 * @param patientId - The provider network patient ID
 * @param caseId - Optional case ID to scope the messaging session
 * @returns { data } on success, { error } on failure
 */
export async function getMessagingSession(
  patientId: string,
  caseId?: string
): Promise<{ data?: MessagingSessionResult; error?: string }> {
  const response = await getMessagingAppUrl({
    path: {
      patient_id: patientId,
    },
    ...(caseId ? { query: { case_id: caseId } } : {}),
  });

  if (response.error) {
    return {
      error: `Failed to get messaging app URL: ${JSON.stringify(response.error)} (Status: ${response.response.status} (${response.response.statusText}))`,
    };
  }

  const data = response.data as {
    auth_link?: string;
    verification_code?: string;
  };

  let caseIdFromUrl: string | null = null;
  if (data?.auth_link) {
    try {
      const url = new URL(data.auth_link);
      caseIdFromUrl = url.searchParams.get("case_id");
    } catch (e) {
      console.error("Error parsing case_id from auth_link:", e);
    }
  }

  if (!data?.verification_code) {
    return { error: "No verification code found in response" };
  }

  return {
    data: {
      chatUrl: data.auth_link || null,
      verificationCode: data.verification_code,
      caseId: caseIdFromUrl,
    },
  };
}
